import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subDays, subHours, differenceInMinutes, format } from "date-fns";
import { Clock } from "lucide-react";

interface ResponseTimeChartProps {
  timeRange: string;
  refreshInterval: number;
}

interface ResponseTimeData {
  time: string;
  avgResponseTime: number;
  medianResponseTime: number;
  count: number;
}

const ResponseTimeChart: React.FC<ResponseTimeChartProps> = ({
  timeRange,
  refreshInterval
}) => {
  const getTimeFilter = () => {
    const now = new Date();
    switch (timeRange) {
      case "1h":
        return subHours(now, 1);
      case "4h":
        return subHours(now, 4);
      case "24h":
        return subDays(now, 1);
      case "7d":
        return subDays(now, 7);
      case "30d":
        return subDays(now, 30);
      default:
        return subDays(now, 7);
    }
  };

  const { data: responseTimeData, isLoading } = useQuery({
    queryKey: ["response-times", timeRange],
    queryFn: async (): Promise<ResponseTimeData[]> => {
      const timeFilter = getTimeFilter();
      
      const { data, error } = await supabase
        .from("notifications")
        .select("created_at, updated_at, status")
        .gte("created_at", timeFilter.toISOString())
        .in("status", ["gesloten", "opgelost"])
        .not("updated_at", "is", null)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Calculate response times and group by intervals
      const getTimeInterval = () => {
        switch (timeRange) {
          case "1h":
          case "4h":
            return 30; // 30 minute intervals
          case "24h":
            return 120; // 2 hour intervals  
          case "7d":
            return 1440; // 1 day intervals
          case "30d":
            return 1440; // 1 day intervals
          default:
            return 120;
        }
      };

      const intervalMinutes = getTimeInterval();
      const intervals: Record<string, {
        responseTimes: number[];
        time: string;
      }> = {};

      data.forEach((notification) => {
        const createdTime = new Date(notification.created_at);
        const updatedTime = new Date(notification.updated_at);
        const responseTimeMinutes = differenceInMinutes(updatedTime, createdTime);
        
        // Only include reasonable response times (less than 1 week)
        if (responseTimeMinutes > 0 && responseTimeMinutes < 10080) {
          const intervalStart = new Date(
            Math.floor(createdTime.getTime() / (intervalMinutes * 60 * 1000)) * intervalMinutes * 60 * 1000
          );
          
          const timeKey = intervalStart.toISOString();
          const timeLabel = timeRange === "7d" || timeRange === "30d" 
            ? format(intervalStart, "dd/MM")
            : intervalStart.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
          
          if (!intervals[timeKey]) {
            intervals[timeKey] = {
              responseTimes: [],
              time: timeLabel
            };
          }
          
          intervals[timeKey].responseTimes.push(responseTimeMinutes);
        }
      });

      return Object.values(intervals)
        .filter(interval => interval.responseTimes.length > 0)
        .map(interval => {
          const sortedTimes = interval.responseTimes.sort((a, b) => a - b);
          const avg = sortedTimes.reduce((sum, time) => sum + time, 0) / sortedTimes.length;
          const median = sortedTimes.length % 2 === 0
            ? (sortedTimes[sortedTimes.length / 2 - 1] + sortedTimes[sortedTimes.length / 2]) / 2
            : sortedTimes[Math.floor(sortedTimes.length / 2)];

          return {
            time: interval.time,
            avgResponseTime: Math.round(avg),
            medianResponseTime: Math.round(median),
            count: sortedTimes.length
          };
        })
        .sort((a, b) => {
          if (timeRange === "7d" || timeRange === "30d") {
            return a.time.localeCompare(b.time);
          }
          return new Date(`2000-01-01 ${a.time}`).getTime() - new Date(`2000-01-01 ${b.time}`).getTime();
        });
    },
    refetchInterval: refreshInterval * 1000,
  });

  const chartConfig = {
    avgResponseTime: {
      label: "Gemiddelde (min)",
      color: "hsl(var(--primary))",
    },
    medianResponseTime: {
      label: "Mediaan (min)", 
      color: "hsl(var(--secondary))",
    },
  };

  const overallAvg = responseTimeData?.length > 0 
    ? Math.round(responseTimeData.reduce((sum, item) => sum + item.avgResponseTime, 0) / responseTimeData.length)
    : 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Reactietijd</CardTitle>
            <CardDescription>Afhandelingstijd van meldingen</CardDescription>
          </div>
          <Clock className="h-4 w-4 text-muted-foreground animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Reactietijd</CardTitle>
          <CardDescription>
            Gemiddeld {overallAvg} minuten
          </CardDescription>
        </div>
        <Clock className="h-4 w-4 text-green-500 animate-pulse" />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={responseTimeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                fontSize={12}
                tickLine={false}
                axisLine={false}
                label={{ value: 'Minuten', angle: -90, position: 'insideLeft' }}
              />
              <ChartTooltip 
                content={<ChartTooltipContent />}
                labelFormatter={(value) => `Tijd: ${value}`}
                formatter={(value: number, name: string) => [
                  `${value} min`,
                  chartConfig[name as keyof typeof chartConfig]?.label || name
                ]}
              />
              <Area
                type="monotone"
                dataKey="avgResponseTime"
                stroke={chartConfig.avgResponseTime.color}
                fill={chartConfig.avgResponseTime.color}
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="medianResponseTime"
                stroke={chartConfig.medianResponseTime.color}
                fill={chartConfig.medianResponseTime.color}
                fillOpacity={0.2}
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Performance indicators */}
        <div className="flex justify-between mt-4 pt-4 border-t text-sm">
          <div className="text-center">
            <div className="font-medium text-green-600">Snel</div>
            <div className="text-xs text-muted-foreground">&lt; 30 min</div>
          </div>
          <div className="text-center">
            <div className="font-medium text-yellow-600">Gemiddeld</div>
            <div className="text-xs text-muted-foreground">30-120 min</div>
          </div>
          <div className="text-center">
            <div className="font-medium text-red-600">Langzaam</div>
            <div className="text-xs text-muted-foreground">&gt; 120 min</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResponseTimeChart;